import React from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import { SignalSlot, SignalStatus } from '../types';
import { SIGNAL_PRESET_COLORS, nextDefaultSignalSlotName, signalSlotNameTaken, uniqueSignalSlotName } from '../createDefaultSignals';
import { useI18n } from '../../i18n';
import { SIGNAL_DISPLAY_MODES } from '../types';

interface SignalSlotEditorProps {
  slot: SignalSlot;
  siblingNames?: string[];
  onSlotChange: (slot: SignalSlot) => void;
  onAddStatus: () => void;
  onDeleteStatus: (statusId: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDeleteSlot?: () => void;
}

function StatusRow({
  status,
  onChange,
  onDelete,
  canDelete,
}: {
  status: SignalStatus;
  onChange: (status: SignalStatus) => void;
  onDelete: () => void;
  canDelete: boolean;
}) {
  const { t } = useI18n();

  return (
    <Box sx={{ p: 1.5, mb: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          label={t('signals.statusName')}
          value={status.name}
          onChange={(e) => onChange({ ...status, name: e.target.value })}
          sx={{ flex: 1, minWidth: 120 }}
        />
        <TextField
          size="small"
          type="color"
          label={t('signals.color')}
          value={status.color}
          onChange={(e) => onChange({ ...status, color: e.target.value })}
          sx={{ width: 72 }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('signals.mode')}</InputLabel>
          <Select
            label={t('signals.mode')}
            value={status.mode}
            onChange={(e) => onChange({ ...status, mode: e.target.value as SignalStatus['mode'] })}
          >
            {SIGNAL_DISPLAY_MODES.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {t(`signals.modes.${mode}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" color="error" disabled={!canDelete} onClick={onDelete}>
          <Delete fontSize="small" />
        </IconButton>
      </Box>
      <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
        {SIGNAL_PRESET_COLORS.map((preset) => (
          <Button
            key={preset.id}
            size="small"
            variant={status.color.toLowerCase() === preset.color.toLowerCase() ? 'contained' : 'outlined'}
            onClick={() => onChange({ ...status, color: preset.color })}
            sx={{
              minWidth: 0,
              px: 1,
              bgcolor: preset.color,
              color: preset.id === 'white' || preset.id === 'yellow' ? '#111' : '#fff',
              borderColor: preset.color,
              '&:hover': { bgcolor: preset.color, filter: 'brightness(0.92)' },
            }}
          >
            {t(preset.labelKey)}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

const SignalSlotEditor: React.FC<SignalSlotEditorProps> = ({
  slot,
  siblingNames = [],
  onSlotChange,
  onAddStatus,
  onDeleteStatus,
  onMoveUp,
  onMoveDown,
  onDeleteSlot,
}) => {
  const { t } = useI18n();

  const updateStatus = (updated: SignalStatus) => {
    onSlotChange({
      ...slot,
      statuses: slot.statuses.map((s) => (s.id === updated.id ? updated : s)),
    });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label={t('signals.slotName')}
          value={slot.name}
          error={signalSlotNameTaken(slot.name, siblingNames)}
          helperText={signalSlotNameTaken(slot.name, siblingNames) ? t('signals.slotNameTaken') : undefined}
          onChange={(e) => onSlotChange({ ...slot, name: e.target.value })}
          onBlur={() => {
            const unique = slot.name.trim()
              ? uniqueSignalSlotName(slot.name, siblingNames)
              : nextDefaultSignalSlotName(siblingNames);
            if (unique !== slot.name) {
              onSlotChange({ ...slot, name: unique });
            }
          }}
          sx={{ flex: 1, minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('signals.previewStatus')}</InputLabel>
          <Select
            label={t('signals.previewStatus')}
            value={slot.previewStatus || slot.statuses[0]?.id || ''}
            onChange={(e) => onSlotChange({ ...slot, previewStatus: String(e.target.value) })}
          >
            {slot.statuses.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {onMoveUp && (
          <IconButton size="small" onClick={onMoveUp}>
            <ArrowUpward fontSize="small" />
          </IconButton>
        )}
        {onMoveDown && (
          <IconButton size="small" onClick={onMoveDown}>
            <ArrowDownward fontSize="small" />
          </IconButton>
        )}
        {onDeleteSlot && (
          <IconButton size="small" color="error" onClick={onDeleteSlot}>
            <Delete fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        {t('signals.statuses')} ({slot.statuses.length})
      </Typography>
      {slot.statuses.map((status) => (
        <StatusRow
          key={status.id}
          status={status}
          onChange={updateStatus}
          onDelete={() => onDeleteStatus(status.id)}
          canDelete={slot.statuses.length > 1}
        />
      ))}
      <Button size="small" startIcon={<Add />} onClick={onAddStatus}>
        {t('signals.addStatus')}
      </Button>
    </Box>
  );
};

export default SignalSlotEditor;

